/**
 * 싱글톤 스킨 로더 서비스
 * 외부 스킨 로딩을 중앙에서 관리하여 중복 로딩 방지 및 캐싱 최적화
 */
class SkinLoaderService {
  constructor() {
    this.loadingPromises = new Map(); // 현재 로딩 중인 요청 관리
    this.loadedSkins = new Map();     // 로드 완료된 스킨 캐시 (키: skinId_version)
    this.skinVersions = new Map();    // 각 스킨의 현재 버전 추적
    this.performanceMetrics = {       // 성능 메트릭
      loadTimes: new Map(),
      successCount: 0,
      failureCount: 0
    };
  }

  /**
   * 스킨 로드 (중복 방지 및 캐싱)
   * @param {string} skinId - 스킨 ID
   * @param {Function} loadFunction - 실제 로드를 수행하는 함수
   * @param {Object} skinData - 스킨 메타데이터 (버전 정보 포함)
   * @returns {Promise<React.Component>} 로드된 스킨 컴포넌트
   */
  async loadSkin(skinId, loadFunction, skinData = {}) {
    const startTime = Date.now();
    
    try {
      // 버전 정보 추출
      const version = this._extractVersion(skinData);
      const cacheKey = `${skinId}_${version}`;
      
      // 1. 이미 로드된 스킨은 캐시에서 반환
      if (this.loadedSkins.has(cacheKey)) {
        this._trackPerformance(skinId, startTime, true, 'cache');
        return this.loadedSkins.get(cacheKey);
      }
      
      // 2. 현재 로딩 중이면 기존 Promise 반환 (중복 로딩 방지)
      if (this.loadingPromises.has(cacheKey)) {
        return this.loadingPromises.get(cacheKey);
      }
      
      // 3. 구버전 캐시 정리
      this._cleanupOldVersion(skinId, version);
      
      // 4. 새로운 로딩 시작
      const loadPromise = this._performLoad(skinId, loadFunction, version);
      this.loadingPromises.set(cacheKey, loadPromise);
      
      // 5. 로딩 완료 처리
      const component = await loadPromise;
      
      // 성공: 캐시에 저장
      this.loadedSkins.set(cacheKey, component);
      this.skinVersions.set(skinId, version);
      this.loadingPromises.delete(cacheKey);
      
      this._trackPerformance(skinId, startTime, true, 'network');

      return component;
      
    } catch (error) {
      const version = this._extractVersion(skinData);
      const cacheKey = `${skinId}_${version}`;
      
      // 실패: 로딩 프로미스 제거
      this.loadingPromises.delete(cacheKey);
      
      this._trackPerformance(skinId, startTime, false, 'error');
      console.error(`[SkinLoader] 로딩 실패: ${cacheKey}`, error);
      
      // 실패율이 높으면 경고
      if (this.getFailureRate() > 0.1) {
        console.warn('[SkinLoader] 스킨 로드 실패율이 10%를 초과했습니다.');
      }
      
      throw error;
    }
  }

  /**
   * 실제 로딩 수행
   */
  async _performLoad(skinId, loadFunction, version) {
    try {
      // 로컬 스토리지에서 메타데이터 확인
      const cachedMetadata = this._getLocalStorageCache(skinId, version);

      
      // 실제 로딩 함수 호출
      const component = await loadFunction(cachedMetadata);
      
      // 로컬 스토리지에 메타데이터 저장
      this._setLocalStorageCache(skinId, version, { timestamp: Date.now() });
      
      // 사용 빈도 추적 (프리로드용)
      this._trackUsage(skinId);
      
      return component;
    } catch (error) {
      console.error(`[SkinLoader] _performLoad 실패: ${skinId}_${version}`, error);
      throw error;
    }
  }

  /**
   * 버전 정보 추출
   */
  _extractVersion(skinData) {
    if (skinData?.latestVersion?.version) {
      return skinData.latestVersion.version;
    }
    if (skinData?.version) {
      return skinData.version;
    }
    return 'latest';
  }

  /**
   * 구버전 캐시 정리
   */
  _cleanupOldVersion(skinId, newVersion) {
    const oldVersion = this.skinVersions.get(skinId);
    if (oldVersion && oldVersion !== newVersion) {
      const oldCacheKey = `${skinId}_${oldVersion}`;
      this.loadedSkins.delete(oldCacheKey);

      // DOM에서도 구버전 제거
      const oldElements = document.querySelectorAll(`[data-skin-id="${skinId}"][data-version="${oldVersion}"]`);
      oldElements.forEach(el => el.remove());
    }
  }

  /**
   * 로컬 스토리지 캐시 관리
   */
  _getLocalStorageCache(skinId, version) {
    try {
      const cache = JSON.parse(localStorage.getItem('withcookie_skin_cache') || '{}');
      const key = `${skinId}_${version}`;
      const item = cache[key];
      
      const MAX_AGE = 24 * 60 * 60 * 1000; // 24시간
      if (item && (Date.now() - item.timestamp < MAX_AGE)) {
        return item.metadata;
      }
    } catch (e) {
      console.warn('[SkinLoader] 로컬 스토리지 읽기 실패:', e);
    }
    return null;
  }

  _setLocalStorageCache(skinId, version, metadata) {
    try {
      const cache = JSON.parse(localStorage.getItem('withcookie_skin_cache') || '{}');
      const key = `${skinId}_${version}`;
      
      cache[key] = {
        metadata,
        timestamp: Date.now()
      };
      
      // 오래된 항목 정리
      this._cleanupLocalStorageCache(cache);
      
      localStorage.setItem('withcookie_skin_cache', JSON.stringify(cache));
    } catch (e) {
      console.warn('[SkinLoader] 로컬 스토리지 쓰기 실패:', e);
    }
  }

  _cleanupLocalStorageCache(cache) {
    const MAX_AGE = 24 * 60 * 60 * 1000; // 24시간
    const now = Date.now();
    
    Object.keys(cache).forEach(key => {
      if (now - cache[key].timestamp > MAX_AGE) {
        delete cache[key];
      }
    });
  }

  /**
   * 사용 빈도 추적 (프리로드용)
   */
  _trackUsage(skinId) {
    try {
      const usage = JSON.parse(localStorage.getItem('skin_usage') || '{}');
      usage[skinId] = (usage[skinId] || 0) + 1;
      localStorage.setItem('skin_usage', JSON.stringify(usage));
    } catch (e) {
      console.warn('[SkinLoader] 사용 빈도 추적 실패:', e);
    }
  }

  /**
   * 성능 추적
   */
  _trackPerformance(skinId, startTime, success, source) {
    const duration = Date.now() - startTime;
    
    this.performanceMetrics.loadTimes.set(skinId, {
      duration,
      success,
      source,
      timestamp: Date.now()
    });
    
    if (success) {
      this.performanceMetrics.successCount++;
    } else {
      this.performanceMetrics.failureCount++;
    }
    
  }

  /**
   * 실패율 계산
   */
  getFailureRate() {
    const total = this.performanceMetrics.successCount + this.performanceMetrics.failureCount;
    if (total === 0) return 0;
    return this.performanceMetrics.failureCount / total;
  }

  /**
   * 캐시 무효화
   */
  invalidate(skinId) {
    // 모든 버전의 캐시 제거
    const keysToDelete = [];
    this.loadedSkins.forEach((_, key) => {
      if (key.startsWith(`${skinId}_`)) {
        keysToDelete.push(key);
      }
    });
    
    keysToDelete.forEach(key => {
      this.loadedSkins.delete(key);
      this.loadingPromises.delete(key);
    });
    
    this.skinVersions.delete(skinId);
    
  }

  /**
   * 자주 사용하는 스킨 목록 (프리로드용)
   */
  getFrequentlyUsedSkins(limit = 5) {
    try {
      const usage = JSON.parse(localStorage.getItem('skin_usage') || '{}');
      return Object.entries(usage)
        .sort(([, a], [, b]) => b - a)
        .slice(0, limit)
        .map(([skinId]) => skinId);
    } catch (e) {
      console.warn('[SkinLoader] 사용 빈도 데이터 읽기 실패:', e);
      return [];
    }
  }

  /**
   * 프리로드 수행
   */
  async preloadSkins(skinIds) {

    skinIds.forEach(skinId => {
      // JS 프리페치
      const scriptLink = document.createElement('link');
      scriptLink.rel = 'prefetch';
      scriptLink.as = 'script';
      scriptLink.href = `/api/skins/${skinId}/latest.js`;
      document.head.appendChild(scriptLink);
      
      // CSS 프리페치
      const cssLink = document.createElement('link');
      cssLink.rel = 'prefetch';
      cssLink.as = 'style';
      cssLink.href = `/api/skins/${skinId}/latest.css`;
      document.head.appendChild(cssLink);
    });
  }
  
  /**
   * 특정 스킨 프리로드
   */
  preloadSkin(skinId) {
    if (!skinId) return;
    
    // 이미 로드되었거나 로딩 중인 경우 스킵
    for (const key of this.loadedSkins.keys()) {
      if (key.startsWith(`${skinId}_`)) return;
    }
    for (const key of this.loadingPromises.keys()) {
      if (key.startsWith(`${skinId}_`)) return;
    }
    
    this.preloadSkins([skinId]);
  }
}

// 싱글톤 인스턴스 생성 및 내보내기
export const skinLoaderService = new SkinLoaderService();

// 페이지 로드 시 자주 사용하는 스킨 프리로드 및 CDN 연결 최적화
if (typeof window !== 'undefined') {
  // CDN에 대한 DNS prefetch와 preconnect 추가 (즉시 실행)
  const cdnHosts = [
    'https://withcookie.b-cdn.net',
    'https://cdn.withcookie-refrigerator.com',
    'https://withcookie-bucket.s3.ap-northeast-2.amazonaws.com'
  ];
  
  cdnHosts.forEach(host => {
    // DNS prefetch
    const dnsPrefetch = document.createElement('link');
    dnsPrefetch.rel = 'dns-prefetch';
    dnsPrefetch.href = host;
    document.head.appendChild(dnsPrefetch);
    
    // Preconnect
    const preconnect = document.createElement('link');
    preconnect.rel = 'preconnect';
    preconnect.href = host;
    preconnect.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect);
  });
  
  window.addEventListener('load', () => {
    const frequentSkins = skinLoaderService.getFrequentlyUsedSkins();
    if (frequentSkins.length > 0) {
      skinLoaderService.preloadSkins(frequentSkins);
    }
  });
}